import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOSchema from '@/components/base/SEOSchema';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryComics = comics.filter((c) => c.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <p className="text-foreground-500">分类未找到</p>
        </div>
        <Footer />
      </>
    );
  }

  const catTitle = `${category.name}韩国漫画`;
  const catDesc = `动漫之家为您精选${category.count}部${category.name}类无删减韩国漫画，下拉式免费阅读，高清画质流畅体验。热门新作实时更新。`;

  const categorySchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${category.name} - 动漫之家韩漫分类`,
      description: catDesc,
      url: `http://hgok7.com/category/${slug}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://hgok7.com/' },
        { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'http://hgok7.com/category/romance' },
        { '@type': 'ListItem', position: 3, name: category.name },
      ],
    },
  ];

  return (
    <>
      <SEOSchema
        schemas={categorySchema}
        canonicalUrl={`http://hgok7.com/category/${slug}`}
        pageTitle={`${category.name}韩国漫画-无删减下拉式韩漫-动漫之家`}
        pageDescription={catDesc}
        pageKeywords={`${category.name},韩国漫画,无删减漫画,下拉式漫画,韩漫在线免费阅读,动漫之家`}
      />

      <Navbar />

      {/* Hero */}
      <header className="relative h-[55vh] w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20art%20style%20dramatic%20collage%2C%20warm%20amber%20and%20rose%20lighting%2C%20romance%20and%20fantasy%20webtoon%20panels%20blending%20together%2C%20cinematic%20atmosphere%20with%20rich%20contrast%2C%20elegant%20digital%20illustration%20with%20soft%20gradients%2C%20emotional%20storytelling%20mood&width=1920&height=800&seq=cat-hero-002&orientation=landscape"
            alt={`${category.name}韩国漫画`}
            title={`${category.name}韩国漫画,韩漫在线免费阅读`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65"></div>
        </div>

        <div className="relative z-10 h-full flex flex-col justify-end pb-12 px-6 md:px-14 lg:px-20">
          <div className="mb-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '漫画分类', href: '/category/romance' },
                { label: category.name },
              ]}
            />
          </div>
          <span className="inline-block px-4 py-1.5 rounded-full border border-white/40 text-white text-xs font-label font-medium tracking-wider mb-3">
            无删减 · 下拉式阅读
          </span>
          <h1 className="text-4xl md:text-6xl font-heading font-light text-white leading-tight tracking-tight">
            {category.name}
          </h1>
          <p className="mt-2 text-sm text-white/70 font-body">
            共 {category.count} 部作品
          </p>
        </div>
      </header>

      {/* Category Tabs */}
      <nav className="sticky top-16 z-40 bg-background-100/95 backdrop-blur-md border-b border-background-200/60" aria-label="分类切换">
        <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
          <div className="flex items-center gap-1 py-3 overflow-x-auto whitespace-nowrap scrollbar-hide">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-5 py-2 rounded-full text-sm font-label font-medium transition-all duration-200 cursor-pointer flex-shrink-0 ${
                  cat.slug === slug
                    ? 'bg-foreground-950 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70'
                }`}
              >
                <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      <main>
        {/* Comic Grid */}
        <section className="bg-background-100 py-12 md:py-20 px-6 md:px-14 lg:px-20">
          <div className="max-w-7xl mx-auto">
            {categoryComics.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {categoryComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.slug}`}
                    className="group block bg-background-50 rounded-2xl overflow-hidden hover:bg-background-50/80 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={comic.coverImage}
                        alt={comic.title}
                        title={`${comic.title} - ${category.name}韩国漫画`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 flex gap-2">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-label font-semibold ${
                          comic.status === '连载中'
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-accent-500 text-background-50'
                        }`}>
                          {comic.status}
                        </span>
                        {comic.isNew && (
                          <span className="px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-label font-semibold">
                            NEW
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-heading font-bold text-foreground-950 mb-1.5">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mb-3">{comic.author}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-primary-500 w-3 h-3 flex items-center justify-center"></i>
                          {comic.rating}
                        </span>
                        <span>{comic.views.toLocaleString()} 阅读</span>
                        <span>{comic.chapters} 话</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}